import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  SafeAreaView,
} from '../utils/webComponents';
import { useNavigation, useRoute } from '../utils/navigation';
import { LibraryBooks, Add, ArrowBack } from '@mui/icons-material';
import { useFlashcards } from '../context/FlashcardContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';

const CreateSetScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { classId } = route.params || {};
  const { addFlashcardSet } = useFlashcards();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [setName, setSetName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreateSet = async () => {
    if (!setName.trim()) {
      return;
    }

    if (!classId) {
      return;
    }

    setLoading(true);

    try {
      const colors = ['#4A90E2', '#7ED321', '#F5A623', '#D0021B', '#9013FE', '#50E3C2'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];

      addFlashcardSet({
        classId,
        name: setName.trim(),
        description: description.trim(),
        color: randomColor,
      });

      navigation.goBack();
    } catch (error) {
      console.error('Error creating set:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Main Content */}
        <View style={styles.contentContainer}>
          <View style={[styles.content, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <View style={styles.iconContainer}>
              <View style={[styles.iconCircle, { backgroundColor: theme.primaryLight }]}>
                <LibraryBooks sx={{ fontSize: 48, color: theme.primary }} />
              </View>
            </View>
            
            <Text style={[styles.title, { color: theme.text }]}>{t('createSet.title')}</Text>

            <View style={styles.form}>
              <View style={styles.inputContainer}>
                <Text style={[styles.inputLabel, { color: theme.text }]}>{t('createSet.setName')}</Text>
                <TextInput
                  style={[styles.textInput, { backgroundColor: theme.inputBackground || theme.surface, color: theme.text, borderColor: theme.border }]}
                  value={setName}
                  onChangeText={setSetName}
                  placeholder={t('createSet.setNamePlaceholder')}
                  placeholderTextColor={theme.textSecondary}
                />
              </View>

              <View style={styles.inputContainer}>
                <Text style={[styles.inputLabel, { color: theme.text }]}>{t('createSet.description')}</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea, { backgroundColor: theme.inputBackground || theme.surface, color: theme.text, borderColor: theme.border }]}
                  value={description}
                  onChangeText={setDescription}
                  placeholder={t('createSet.descriptionPlaceholder')}
                  placeholderTextColor={theme.textSecondary}
                  multiline
                  numberOfLines={4}
                />
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={() => navigation.goBack()}
              >
                <Text style={[styles.cancelButtonText, { color: theme.text }]}>{t('createSet.cancel')}</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.button, styles.createButton, { backgroundColor: theme.primary }, loading && styles.disabledButton]}
                onPress={handleCreateSet}
                disabled={loading || !setName.trim()}
              >
                <Add sx={{ fontSize: 20, color: 'white' }} />
                <Text style={[styles.createButtonText, { color: 'white' }]}>
                  {loading ? t('createSet.creating') : t('createSet.create')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
    display: 'flex',
  },
  scrollContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    minHeight: '100vh',
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 600,
  },
  content: {
    width: '100%',
    maxWidth: 600,
    padding: 32,
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    margin: '0 auto',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 32,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 32,
  },
  form: {
    marginBottom: 32,
  },
  inputContainer: {
    marginBottom: 24,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  textInput: {
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    borderWidth: 1,
    width: '100%',
    transition: 'border-color 0.2s',
  },
  textArea: {
    minHeight: 100,
    resize: 'vertical',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s, opacity 0.2s',
    cursor: 'pointer',
  },
  cancelButton: {
    borderWidth: 1,
  },
  createButton: {
    gap: 8,
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CreateSetScreen;
